// src/components/settings/TwitterOnboardingWizard.jsx
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ArrowRightIcon,
    KeyIcon,
    ShareIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STEPS = {
    WELCOME: 'welcome',
    CREDENTIALS: 'credentials',
    VERIFICATION: 'verification',
    COMPLETE: 'complete'
};

const TwitterOnboardingWizard = ({ onComplete, onClose }) => {
    const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
    const [credentials, setCredentials] = useState({
        TWITTER_API_KEY: '',
        TWITTER_API_SECRET: '',
        TWITTER_ACCESS_TOKEN: '',
        TWITTER_ACCESS_TOKEN_SECRET: '',
        TWITTER_BEARER_TOKEN: ''
    });
    const [validationResult, setValidationResult] = useState(null);
    const [isTestingConnection, setIsTestingConnection] = useState(false);

    const queryClient = useQueryClient();

    // Save credentials mutation
    const saveCredentialsMutation = useMutation(
        (creds) => settingsAPI.saveCredentials(creds),
        {
            onSuccess: () => {
                toast.success('Twitter credentials saved!');
                queryClient.invalidateQueries('credentials');
            },
            onError: (error) => {
                toast.error('Failed to save credentials: ' + error.message);
            }
        }
    );

    // Test connection mutation
    const testConnectionMutation = useMutation(
        () => settingsAPI.testTwitter(),
        {
            onSuccess: (data) => {
                setValidationResult(data);
                setCurrentStep(STEPS.COMPLETE);
                toast.success('Twitter connection verified!');
            },
            onError: (error) => {
                setValidationResult({ success: false, message: error.message });
                toast.error('Connection test failed: ' + error.message);
            }
        }
    );

    const handleCredentialChange = (key, value) => {
        setCredentials(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSaveAndTest = async () => {
        setIsTestingConnection(true);
        try {
            // Save credentials first
            await saveCredentialsMutation.mutateAsync(credentials);

            // Then test the connection
            await testConnectionMutation.mutateAsync();
        } catch (error) {
            console.error('Failed to save and test:', error);
        } finally {
            setIsTestingConnection(false);
        }
    };

    const isStepComplete = (step) => {
        switch (step) {
            case STEPS.WELCOME:
                return true;
            case STEPS.CREDENTIALS:
                return credentials.TWITTER_API_KEY && credentials.TWITTER_API_SECRET &&
                    credentials.TWITTER_ACCESS_TOKEN && credentials.TWITTER_ACCESS_TOKEN_SECRET;
            case STEPS.VERIFICATION:
                return validationResult?.success;
            default:
                return false;
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case STEPS.WELCOME:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                <ShareIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Connect Your Twitter Account
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                Set up Twitter/X integration to automatically post your content to your Twitter account.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">What you'll need:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>A Twitter Developer account</li>
                                        <li>A Twitter app with API v1.1 and v2 access</li>
                                        <li>Your app's API keys and access tokens</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.CREDENTIALS)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                            >
                                Get Started
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.CREDENTIALS:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Enter Your Twitter API Credentials
                            </h2>
                            <p className="text-gray-600">
                                Copy and paste your Twitter app credentials from the{' '}
                                <a href="https://developer.twitter.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Twitter Developer Portal
                                </a><br/><br/>
                                When asked to Describe all of your use cases of Tritter's data and API:<br/>
                                <div className="text-sm bg-gray-500 p-2 text-white rounded-2xl mt-2">
                                    <b>Social Media posting using PostMgr. Helping engage with a range of users. Post images, copy, and use the X platform to help expand our social media presence. Read, Write, and maybe interact with posters. Build a social following and promote the Twitter / X platform.</b></div>
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    API Key *
                                </label>
                                <input
                                    type="text"
                                    value={credentials.TWITTER_API_KEY}
                                    onChange={(e) => handleCredentialChange('TWITTER_API_KEY', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your Twitter API Key"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    API Secret Key *
                                </label>
                                <input
                                    type="password"
                                    value={credentials.TWITTER_API_SECRET}
                                    onChange={(e) => handleCredentialChange('TWITTER_API_SECRET', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your Twitter API Secret Key"
                                />
                            </div>
                            <div className="p-5">
                                <b>Permissions:</b>
                                <div>
                                    <li>Before generating the Access Token, Access Token Secret, and Bearer Token we need to goto grant <b>Read/Write</b> permissions.</li>
                                    <li>Click "Set up" under <b>User authentication settings</b></li>
                                    <li><b>App permissions</b> select "Read and write".</li>
                                    <li><b>Type of App</b> select "Web App, Automated App or Bot</li>
                                    <li><b>App Info</b>Callback URI / Redirect URL: https://postmgr.com/auth/twitter/callback</li>
                                    <li><b>App Info</b>Website URL: https://postmgr.com/</li>
                                    <li>Then click "Save"</li>
                                    <li>You can save your OAuth 2.0 Credentials, but they are not required for this app.</li>
                                    <li>Click "Done"</li>
                                    <li>Go back to the <b>Keys and tokens</b> tab and generate you Bearer and Access Tokens now and place them below.</li>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Access Token *
                                </label>
                                <input
                                    type="text"
                                    value={credentials.TWITTER_ACCESS_TOKEN}
                                    onChange={(e) => handleCredentialChange('TWITTER_ACCESS_TOKEN', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your Twitter Access Token"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Access Token Secret *
                                </label>
                                <input
                                    type="password"
                                    value={credentials.TWITTER_ACCESS_TOKEN_SECRET}
                                    onChange={(e) => handleCredentialChange('TWITTER_ACCESS_TOKEN_SECRET', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your Twitter Access Token Secret"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Bearer Token (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={credentials.TWITTER_BEARER_TOKEN}
                                    onChange={(e) => handleCredentialChange('TWITTER_BEARER_TOKEN', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your Twitter Bearer Token (for API v2)"
                                />
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Setup Requirements:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Your Twitter app needs "Read and Write" permissions</li>
                                        <li>Ensure your app has access to Twitter API v1.1</li>
                                        <li>Bearer Token is optional but recommended for API v2 features</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.WELCOME)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.VERIFICATION)}
                                disabled={!isStepComplete(STEPS.CREDENTIALS)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                            >
                                Continue
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.VERIFICATION:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Test Your Connection
                            </h2>
                            <p className="text-gray-600">
                                Let's verify that your Twitter credentials are working correctly.
                            </p>
                        </div>

                        {validationResult && !validationResult.success && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-red-800">Connection Failed</h3>
                                        <p className="text-sm text-red-700 mt-1">{validationResult.message}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.CREDENTIALS)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleSaveAndTest}
                                disabled={isTestingConnection}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
                            >
                                {isTestingConnection ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Testing...
                                    </>
                                ) : (
                                    <>
                                        Test Connection
                                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                );

            case STEPS.COMPLETE:
                return (
                    <div className="space-y-6 text-center">
                        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Twitter Connected Successfully!
                            </h2>
                            <p className="text-gray-600">
                                Your Twitter account is now connected and ready for posting.
                            </p>
                        </div>

                        {validationResult?.data && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="text-sm text-green-800">
                                    <p className="font-medium">Connection Details:</p>
                                    <p>✅ API credentials verified</p>
                                    <p>✅ Ready to post tweets</p>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={onComplete}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                        >
                            Complete Setup
                            <ArrowRightIcon className="ml-2 h-5 w-5" />
                        </button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    {/* Progress bar */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <h1 className="text-lg font-medium text-gray-900">Twitter Setup</h1>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex items-center space-x-2">
                            {Object.values(STEPS).map((step, index) => (
                                <React.Fragment key={step}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                        currentStep === step
                                            ? 'bg-blue-600 text-white'
                                            : isStepComplete(step)
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        {isStepComplete(step) ? '✓' : index + 1}
                                    </div>
                                    {index < Object.values(STEPS).length - 1 && (
                                        <div className={`h-1 w-8 rounded ${
                                            isStepComplete(step) ? 'bg-green-500' : 'bg-gray-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        {renderStepContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwitterOnboardingWizard;